import type { ComponentType, CSSProperties } from 'react';
import {
  Mail,
  Phone,
  Globe,
  Linkedin,
  Instagram,
  Facebook,
  Github,
  Youtube,
  Twitch,
  MapPin,
  Calendar,
  CalendarClock,
  FileText,
  ShoppingBag,
  Link as LinkIcon,
} from 'lucide-react';
import {
  TikTokIcon,
  XIcon,
  ThreadsIcon,
  SnapchatIcon,
  PinterestIcon,
  WhatsAppIcon,
  TelegramIcon,
  DiscordIcon,
  BlueskyIcon,
} from './brandIcons';

export type LinkType =
  | 'email'
  | 'phone'
  | 'website'
  | 'linkedin'
  | 'twitter'
  | 'instagram'
  | 'facebook'
  | 'tiktok'
  | 'threads'
  | 'youtube'
  | 'snapchat'
  | 'pinterest'
  | 'whatsapp'
  | 'telegram'
  | 'discord'
  | 'bluesky'
  | 'twitch'
  | 'github'
  | 'address'
  | 'location'
  | 'calendar'
  | 'booking'
  | 'document'
  | 'product'
  | 'other';

/** Anything that renders like a Lucide icon — Lucide or one of our brand glyphs. */
export type IconComponent = ComponentType<{ className?: string; style?: CSSProperties }>;

export interface LinkTypeMeta {
  type: LinkType;
  label: string;
  icon: IconComponent;
  prefix?: string;
  placeholder: string;
  /**
   * Turns a bare handle ("@roger", "roger") into the profile URL. When set,
   * the editor accepts either a handle or a full URL in the value field.
   */
  profileUrl?: (handle: string) => string;
  /** Social profile — shows in the Socials icon row and the vCard. */
  social?: boolean;
}

const handle = (v: string) => v.trim().replace(/^@/, '');

export const LINK_TYPES: LinkTypeMeta[] = [
  { type: 'email', label: 'Email', icon: Mail, prefix: 'mailto:', placeholder: 'you@domain.com' },
  { type: 'phone', label: 'Phone', icon: Phone, prefix: 'tel:', placeholder: '+1 555 000 0000' },
  { type: 'website', label: 'Website', icon: Globe, placeholder: 'https://...' },
  {
    type: 'instagram',
    label: 'Instagram',
    icon: Instagram,
    placeholder: '@handle or URL',
    profileUrl: (h) => `https://instagram.com/${handle(h)}`,
    social: true,
  },
  {
    type: 'tiktok',
    label: 'TikTok',
    icon: TikTokIcon,
    placeholder: '@handle or URL',
    profileUrl: (h) => `https://www.tiktok.com/@${handle(h)}`,
    social: true,
  },
  {
    type: 'linkedin',
    label: 'LinkedIn',
    icon: Linkedin,
    placeholder: 'https://linkedin.com/in/... or username',
    profileUrl: (h) => `https://www.linkedin.com/in/${handle(h)}`,
    social: true,
  },
  {
    type: 'twitter',
    label: 'X (Twitter)',
    icon: XIcon,
    placeholder: '@handle or URL',
    profileUrl: (h) => `https://x.com/${handle(h)}`,
    social: true,
  },
  {
    type: 'threads',
    label: 'Threads',
    icon: ThreadsIcon,
    placeholder: '@handle or URL',
    profileUrl: (h) => `https://www.threads.net/@${handle(h)}`,
    social: true,
  },
  {
    type: 'facebook',
    label: 'Facebook',
    icon: Facebook,
    placeholder: 'https://facebook.com/... or page name',
    profileUrl: (h) => `https://www.facebook.com/${handle(h)}`,
    social: true,
  },
  {
    type: 'youtube',
    label: 'YouTube',
    icon: Youtube,
    placeholder: '@channel or URL',
    profileUrl: (h) => `https://www.youtube.com/@${handle(h)}`,
    social: true,
  },
  {
    type: 'snapchat',
    label: 'Snapchat',
    icon: SnapchatIcon,
    placeholder: 'username or URL',
    profileUrl: (h) => `https://www.snapchat.com/add/${handle(h)}`,
    social: true,
  },
  {
    type: 'pinterest',
    label: 'Pinterest',
    icon: PinterestIcon,
    placeholder: 'username or URL',
    profileUrl: (h) => `https://www.pinterest.com/${handle(h)}`,
    social: true,
  },
  {
    type: 'whatsapp',
    label: 'WhatsApp',
    icon: WhatsAppIcon,
    placeholder: '+1 555 000 0000 or wa.me link',
    profileUrl: (h) => `https://wa.me/${h.replace(/[^0-9]/g, '')}`,
    social: true,
  },
  {
    type: 'telegram',
    label: 'Telegram',
    icon: TelegramIcon,
    placeholder: '@username or URL',
    profileUrl: (h) => `https://t.me/${handle(h)}`,
    social: true,
  },
  {
    type: 'discord',
    label: 'Discord',
    icon: DiscordIcon,
    placeholder: 'Invite link (discord.gg/…)',
    social: true,
  },
  {
    type: 'bluesky',
    label: 'Bluesky',
    icon: BlueskyIcon,
    placeholder: 'handle.bsky.social or URL',
    profileUrl: (h) => `https://bsky.app/profile/${handle(h)}`,
    social: true,
  },
  {
    type: 'twitch',
    label: 'Twitch',
    icon: Twitch,
    placeholder: 'channel or URL',
    profileUrl: (h) => `https://www.twitch.tv/${handle(h)}`,
    social: true,
  },
  {
    type: 'github',
    label: 'GitHub',
    icon: Github,
    placeholder: 'username or URL',
    profileUrl: (h) => `https://github.com/${handle(h)}`,
    social: true,
  },
  { type: 'address', label: 'Address', icon: MapPin, placeholder: '123 Market St' },
  { type: 'location', label: 'Location', icon: MapPin, placeholder: 'Search a business or paste a Google Maps link' },
  { type: 'calendar', label: 'Calendar', icon: Calendar, placeholder: 'https://cal.com/...' },
  { type: 'booking', label: 'Booking', icon: CalendarClock, placeholder: 'https://calendly.com/... or book.squareup.com/...' },
  { type: 'document', label: 'Document', icon: FileText, placeholder: 'https://...' },
  { type: 'product', label: 'Product', icon: ShoppingBag, placeholder: 'Image URL' },
  { type: 'other', label: 'Other', icon: LinkIcon, placeholder: 'https://...' },
];

/** Every social profile type, in display order. */
export const SOCIAL_LINK_TYPES: LinkType[] = LINK_TYPES.filter((t) => t.social).map((t) => t.type);

export function linkMeta(type: string): LinkTypeMeta {
  return LINK_TYPES.find((l) => l.type === type) ?? LINK_TYPES[LINK_TYPES.length - 1];
}

/**
 * True when a social value reads as a bare handle rather than a URL.
 * Handles routinely contain dots ("@perrett.studio", "name.bsky.social"),
 * so a dot alone doesn't make it a domain — only a scheme, a slash, or a
 * leading "www." does.
 */
function looksLikeHandle(value: string): boolean {
  const v = value.trim();
  if (!v) return false;
  if (v.startsWith('@')) return true;
  if (/^[a-z][a-z0-9+.-]*:/i.test(v)) return false; // has a scheme
  if (v.includes('/')) return false;
  if (/^www\./i.test(v)) return false;
  return true;
}

export function linkHref(type: string, value: string): string {
  const meta = linkMeta(type);
  // mailto: / tel: prefixes — auto-prepend if missing.
  if (meta.prefix) {
    return value.startsWith(meta.prefix) ? value : `${meta.prefix}${value}`;
  }
  // Address / location values are freeform text (e.g. "123 Main St") — not URLs.
  if (type === 'address' || type === 'location') return value;
  // Social handle → profile URL.
  if (meta.profileUrl) {
    const v = value.trim();
    if (type === 'whatsapp') {
      if (!/^https?:/i.test(v) && !/wa\.me/i.test(v)) return meta.profileUrl(v);
    } else if (looksLikeHandle(v)) {
      return meta.profileUrl(v);
    }
  }
  // Everything else is URL-shaped. If the user saved "www.example.com" or
  // "example.com", the browser would resolve <a href="..."> as a path
  // relative to gethaphe.com, which 404s. Ensure an https:// scheme.
  return ensureHttpScheme(value);
}

/**
 * Add `https://` to a value that looks like a URL but is missing its scheme.
 * Leaves already-schemed URLs (`http:`, `https:`, `mailto:`, `tel:`, custom)
 * and protocol-relative / absolute-path / anchor values untouched.
 */
export function ensureHttpScheme(value: string): string {
  const v = value.trim();
  if (!v) return v;
  if (/^[a-z][a-z0-9+.-]*:/i.test(v)) return v;        // any scheme
  if (v.startsWith('//')) return v;                     // protocol-relative
  if (v.startsWith('#') || v.startsWith('/')) return v; // anchor / absolute path
  return `https://${v}`;
}
